import { Component, OnDestroy, OnInit, Signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import {
  selectApplication,
  selectLoading,
  selectPending
} from '../../../../../store/selectors/application.selectors';
import { ReactiveFormsModule } from '@angular/forms';
import { LocationDataForm } from './forms/location-data.form';
import { SpinnerComponent } from '../../../../../common/spinner/spinner.component';
import { ActivatedRoute, Router } from '@angular/router';
import { updateParcels } from '../../../../../store/actions/application.actions';
import { ValidationErrorsComponent } from '../../../../../common/validation-errors/validation-errors.component';
import { Subscription } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ApplicationAppEntityApplicationRead } from '../../../../../openapi';
import { StagesComponent } from '../../../../../common/stages/stages.component';
import { DatePickerComponent } from '../../../../../common/date-picker/date-picker.component';
import { ParcelsCrudComponent } from '../components/parcels/parcels-crud/parcels-crud.component';
import { NgxMaskDirective } from 'ngx-mask';

export interface LocationDataComponentConfig {
  stages: string[];
  successUrl: string;
  cancelUrl: string;
  addPostInvestmentUsageControls?: boolean;
  addDecisionControls?: boolean;
  addReferenceVehicleTypeControl?: boolean;
  addPropertyParkingSpacesCountControl?: boolean;
}

@Component({
  selector: 'app-location-data-section',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SpinnerComponent,
    ValidationErrorsComponent,
    StagesComponent,
    DatePickerComponent,
    ParcelsCrudComponent,
    NgxMaskDirective
  ],
  providers: [LocationDataForm],
  templateUrl: './location-data-section.component.html'
})
export class LocationDataSectionComponent implements OnInit, OnDestroy {

  public successUrl: string | undefined = this.route.snapshot.data['successUrl'];

  public cancelUrl: string | undefined = this.route.snapshot.data['cancelUrl'];

  public stages: string[] = this.route.snapshot.data['stages'];

  public applicationId: string | undefined = this.route.snapshot.params['appId'];

  public pending: Signal<boolean> = this.store$.selectSignal(selectPending);

  public loading: Signal<boolean> = this.store$.selectSignal(selectLoading);

  public application: Signal<ApplicationAppEntityApplicationRead | null> = this.store$.selectSignal(selectApplication);

  private fetchApplication: Subscription = toObservable(this.application).subscribe(app => {
    if (app) {
      this.form.patchFormValues(app);
    }
  });

  constructor(
    private store$: Store,
    private router: Router,
    private route: ActivatedRoute,
    public form: LocationDataForm
  ) {
  }

  public submitAndGoNext(): void {
    const currentApp = this.application();
    if (this.form.valid && currentApp) {
      this.store$.dispatch(updateParcels({
        currentApp,
        payload: this.form.toWriteModel(),
        parcels: this.form.parcelsValues,
        successFnc: (applicationId: string) => {
          this.router.navigate(
            [this.successUrl],
            {
              relativeTo: this.route
            }
          );
        },
        failureFnc: () => {
        }
      }));
    } else {
      this.form.markAllAsTouched();
    }
  }

  public onCancel(): void {
    this.router.navigate([this.cancelUrl], { relativeTo: this.route });
  }

  public ngOnInit(): void {
    if (!!this.route.snapshot.data['addPostInvestmentUsageControls']) {
      this.form.createPostInvestmentUsageControl();
    }
    if (!!this.route.snapshot.data['addDecisionControls']) {
      this.form.createDecisionControls();
    }
    if (!!this.route.snapshot.data['addReferenceVehicleTypeControl']) {
      this.form.createReferenceVehicleTypeControl();
    }
    if (!!this.route.snapshot.data['addPropertyParkingSpacesCountControl']) {
      this.form.createPropertyParkingSpacesCountControl();
    }
  }

  public ngOnDestroy(): void {
    this.fetchApplication.unsubscribe();
  }
}
